import {
  createTheme,
  LinkProps,
  Theme as MuiTheme,
  Shadows,
  ThemeOptions,
  inputLabelClasses,
} from "@mui/material";
import { checkboxClasses } from "@mui/material/Checkbox";
import { inputBaseClasses } from "@mui/material/InputBase";
import { gridClasses } from "@mui/x-data-grid";
// eslint-disable-next-line import/order
import { forwardRef } from "react";
import type {} from "@mui/x-data-grid/themeAugmentation";

import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from "react-router-dom";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { EAgencies } from "../types/common.types";

declare module "@mui/material/styles" {
  interface IAdditionalColors {
    oldText: Partial<Palette["primary"]>;
    badges: {
      agency: Record<EAgencies, string>;
      partner: {
        nda: string;
        edin: string;
        birthday: string;
      };
      contract: {
        complex: string;
        virtual: string;
        agreement: string;
        easyService: string;
        easyServiceIC: string;
      };
    };
  }
  interface Palette extends IAdditionalColors {}
  interface PaletteOptions extends Partial<IAdditionalColors> {}
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    neutralAccent: true;
    transparent: true;
  }
}

// fix theme for styled component args
declare module "@emotion/react" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface Theme extends MuiTheme {}
}

const LinkBehavior = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"]; state: unknown }
>((props, ref) => {
  const { href, ...other } = props;

  return <RouterLink ref={ref} to={href} {...other} />;
});

LinkBehavior.displayName = "LinkBehavior";

const shadows: Shadows = [
  "none",
  "0px 1px 4px rgba(92, 92, 92, 0.12)",
  "0px 1px 8px rgba(92, 92, 92, 0.12)",
  "6px 4px 14px rgba(92, 92, 92, 0.12)",
  "-6px 4px 14px rgba(92, 92, 92, 0.12)",
  "0px 5px 30px rgba(0, 0, 0, 0.09)",

  "0px 4px 14px rgba(92, 92, 92, 0.12)",
  "0px -4px 14px rgba(92, 92, 92, 0.12)",
  "0px 0px 13px 0px rgba(133, 133, 133, 0.45)",
  "0px 1px 12px 0px rgba(0, 0, 0, 0.12)",
  "0px -10px 20px 0px rgba(35, 33, 42, 0.05)",
  "0px 1px 4px rgba(0, 0, 0, 0.50)",
  "0px 1px 5px 0px rgba(0, 0, 0, 0.24)",
  "0px 6px 20px 0px rgba(0, 0, 0, 0.10)",
  "none",
  "none",
  "none",
  "none",
  "none",
  "none",
  "none",
  "none",
  "none",
  "none",
  "none",
];

export const themeSettings: ThemeOptions = {
  shadows,
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
      xxl: 1536,
    },
  },
  spacing: 4,
  palette: {
    grey: {
      50: "#F9FAFB",
    },
    oldText: { main: "#1c1c1c" },
    badges: {
      agency: {
        [EAgencies.IContext]: "#00C6C3",
        [EAgencies.Digital]: "#00C603",
        [EAgencies.Webcom_BY]: "#00b9fe",
        [EAgencies.Webcom_RU]: "#00b9fe",
        [EAgencies.Webcom_KZ]: "#00b9fe",
      },
      partner: {
        edin: "#b290d2",
        nda: "#F9f0B9",
        birthday: "#fd010a",
      },
      contract: {
        virtual: "#772f05",
        complex: "#3db95d",
        agreement: "#2c57b9",
        easyService: "#2c57b9",
        easyServiceIC: "#2cb9a5",
      },
    },
  },
  components: {
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          [`& .${inputBaseClasses.input}`]: {
            ...(ownerState.size === "xs" ? { padding: theme.spacing(1) } : {}),
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        sizeSmall: {
          [`&:not(.${inputLabelClasses.shrink})`]: {
            transform: "translate(14px, 8px) scale(1)",
          },
        },
        root: {
          maxWidth: "calc(100% - 36px)",
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        // disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
        underline: "none",
      } as LinkProps,
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: "24px",
          boxShadow: theme.shadows[5],
          padding: theme.spacing(2, 2, 2, 4),
          minHeight: theme.spacing(12),
          margin: "0 auto",
          flexWrap: "nowrap",
          backgroundColor: theme.palette.background.default,
        }),
        message: {
          padding: 0,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          flexWrap: "nowrap",
          fontWeight: 600,
          lineHeight: 1.25,
          fontSize: "0.875rem",
        },
        action: ({ theme }) => ({
          marginRight: 0,
          paddingLeft: theme.spacing(3),
        }),
      },
    },
    MuiDataGrid: {
      defaultProps: {
        disableColumnMenu: true,
        disableColumnResize: true,
        showColumnVerticalBorder: true,
        showCellVerticalBorder: true,
        disableRowSelectionOnClick: true,
      },
      styleOverrides: {
        root: {
          ["&.no-cell-selection"]: {
            [`& .${gridClasses.cell}`]: {
              cursor: "pointer",
            },
            [`& .${gridClasses.cell}:focus`]: {
              outline: "none",
            },
            [`& .${gridClasses.cell}:focus-within`]: {
              outline: "none",
            },
          },
        },
        columnHeader: ({ theme }) => ({
          backgroundColor: theme.palette.grey[200],
          [`& .${gridClasses.iconButtonContainer}`]: {
            order: 0,
          },
          [`& .${gridClasses.iconButtonContainer}`]: {
            visibility: "visible",
            width: "auto",
          },
          [`&.no-border-right`]: {
            borderRight: "none",
          },
        }),
        columnHeaderTitle: {
          whiteSpace: "normal",
          lineHeight: "1.2",
        },
        cell: {
          [`&.no-border-right`]: {
            borderRight: "none",
          },
          [`&.vertical-align-center`]: {
            display: "flex",
            alignItems: "center",
          },
          [`&.center-item`]: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        },
        footerContainer: ({ theme }) => ({
          minHeight: 44,
          backgroundColor: theme.palette.grey[200],
          borderRadius: theme.spacing(0, 0, 4, 4),
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`&.${checkboxClasses.disabled}`]: {
            color: theme.palette.grey[400],
          },
        }),
      },
    },
  },
};

export const theme = createTheme(themeSettings);

declare module "@mui/material/TextField" {
  interface TextFieldPropsSizeOverrides {
    xs: true;
  }
}
